import axios, { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../config/apiConfig';
import { APP_CONFIG } from '../config/appConfig';
import { reportClientError } from '../utils/errorReporting';

// Create axios instance with default timeout
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  timeout: APP_CONFIG.api.timeout, // Default timeout, will be updated by settings
});

// Function to update API timeout dynamically
export const updateApiTimeout = (timeout: number) => {
  api.defaults.timeout = timeout;
};

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Let the browser set multipart boundary — a bare multipart/form-data breaks file uploads.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const headers = config.headers;
      if (headers) {
        if (typeof headers.delete === 'function') {
          headers.delete('Content-Type');
          headers.delete('content-type');
        } else {
          delete (headers as Record<string, unknown>)['Content-Type'];
          delete (headers as Record<string, unknown>)['content-type'];
        }
      }
    }
    // Binary file-send chunks: keep application/octet-stream (do not force JSON).
    if (typeof Blob !== 'undefined' && config.data instanceof Blob) {
      const headers = config.headers;
      if (headers && !headers['Content-Type'] && !headers['content-type']) {
        headers['Content-Type'] = 'application/octet-stream';
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const message = 
      (error.response?.data as any)?.message || 
      error.message || 
      'An error occurred';

    // Handle 401 Unauthorized — try refresh token once before redirecting
    if (status === 401) {
      const originalRequest = error.config as typeof error.config & { _retry?: boolean };
      const isAuthEndpoint =
        originalRequest?.url?.includes('/auth/login') ||
        originalRequest?.url?.includes('/auth/refresh') ||
        originalRequest?.url?.includes('/auth/2fa/verify');

      if (!originalRequest?._retry && !isAuthEndpoint && localStorage.getItem('refresh_token')) {
        originalRequest._retry = true;
        const { authService } = await import('./authService');
        try {
          const result = await authService.refreshAccessToken();
          if (result.status === 'success' && originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${result.data.access_token}`;
            return api(originalRequest);
          }
          if (authService.getRefreshToken()) {
            return Promise.reject(error);
          }
        } catch {
          if (authService.getRefreshToken()) {
            return Promise.reject(error);
          }
          // fall through to logout redirect
        }
      }

      if (localStorage.getItem('refresh_token')) {
        return Promise.reject(error);
      }

      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      const isPublicPage =
        window.location.pathname === '/login' ||
        window.location.pathname === '/register' ||
        window.location.pathname === '/forgot-password' ||
        window.location.pathname === '/reset-password' ||
        window.location.pathname.startsWith('/s/');
      if (!isPublicPage) {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    // Handle 403 Forbidden
    if (status === 403) {
      console.error('Forbidden:', message);
      toast.error(message || 'You don\'t have permission to perform this action.', { duration: 5000 });
      return Promise.reject(error);
    }

    // Handle 404 Not Found
    if (status === 404) {
      const skipToast = (error.config as { skipGlobalErrorToast?: boolean })?.skipGlobalErrorToast;
      if (!skipToast) {
        console.error('Not Found:', message);
        toast.error(message || 'Resource not found.', { duration: 5000 });
      }
      return Promise.reject(error);
    }

    // Handle 500+ Server Errors
    if (status && status >= 500) {
      console.error('Server Error:', message);
      reportClientError({
        message: `API ${status} ${error.config?.method?.toUpperCase() ?? ''} ${error.config?.url ?? ''}: ${message}`,
        stack: error.stack,
      });
      toast.error(message || 'Server error. Please try again later.', { duration: 5000 });
      return Promise.reject(error);
    }

    // Handle validation errors (400)
    if (status === 400) {
      console.error('Validation Error:', message);
      toast.error(message || 'Validation error.', { duration: 5000 });
      return Promise.reject(error);
    }

    // Default error handling
    console.error('API Error:', message);
    toast.error(message || 'An error occurred.', { duration: 5000 });
    return Promise.reject(error);
  }
);

// Export the instance
export default api;
